import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { nanoid } from 'nanoid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Form, Formik } from 'formik';
import Yup from 'utils/validation/yup-extensions';
import FormControlCustomized from '../layouts/FormControlCustomized';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import { submitJobCategoryMappingForm } from '../_api';
import { JobCategoryMappingNewDTO, JobCategoryMappingDTO } from '../_classes';
import { BEM_NAME_ELEMENTS, JOB_CATEGORY_MAPPING_KEYS } from '../_const';
import HRJobTitleAsyncAutocomplete from '../components/HRJobTitleAsyncAutocomplete';
import HRDepartmentAsyncAutocomplete from '../components/HRDepartmentAsyncAutocomplete';
import { FieldArray } from 'formik';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import JobCategoryAsyncAutocomplete2 from '../components/JobCategoryAsyncAutocomplete2';

/**
 * Create a Job Category Mapping
 * @param {object} maintenanceInformation currently logged in user information
 * @returns job category mapping creation form
 */
const FormView = ({ maintenanceInformation, jobCategoryMapping }) => {
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);
	const [sDepartmentKey, setDepartmentKey] = useState(nanoid());
	const [sJobTitleKey, setJobTitleKey] = useState(nanoid());
	const history = useHistory();

	// Multiple job category IDs
	const jobCategoryIDs = jobCategoryMapping?.map(
		mapping => mapping.jobCategoryID
	);

	// Use the multiple job category ids to display the data, otherwise if creating a new one, display new JobCategoryMappingNewDTO
	const initialValues =
		jobCategoryMapping?.length > 0
			? {
					jobCategoryIDs: jobCategoryIDs,
					department: jobCategoryMapping[0].department.label,
					jobTitle: jobCategoryMapping[0].jobTitle.label,
					...new JobCategoryMappingDTO(
						jobCategoryMapping[0]
					).extractValues(),
			  }
			: { ...new JobCategoryMappingNewDTO().extractValues() };

	return (
		<>
			<Formik
				initialValues={initialValues}
				validationSchema={Yup.object().shape({
					[JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT]: Yup.object()
						.shape({
							id: Yup.number(),
							label: Yup.string(),
						})
						.nullable()
						.required('Department is required.'),
					[JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE]: Yup.object()
						.shape({
							id: Yup.number(),
							label: Yup.string(),
						})
						.nullable()
						.required('Job Title is required'),
					[JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS]:
						Yup.array().test(
							'checkShape',
							'You must have at least one job category.',
							value => {
								if (!Array.isArray(value)) {
									return false;
								}
								if(value.length === 0){
									return false;
								}
								if (
									!value.every(
										el =>
											typeof el === 'object' &&
											typeof el.id === 'number' &&
											el.id > 0 &&
											typeof el.label === 'string' &&
											el.label !== ''
									)
								) {
									return false;
								}
								return true;
							}
						),
				})}
				validateOnMount
				onSubmit={async (values, actions) => {
					actions.setSubmitting(true);
					await submitJobCategoryMappingForm(
						{
							...new JobCategoryMappingNewDTO(values).prepare(
								maintenanceInformation,
								true
							),
						},
						true
					)
						.then(response => {
							actions.setSubmitting(false);
							let displayMessage =
								response.data.payload === false
									? 'Submitted Unsuccessfully'
									: 'Submitted Successfully';
							if (
								response?.data?.messages &&
								response.data.messages.length > 0 &&
								response.data.messages[0].Value
							) {
								displayMessage =
									response.data.messages[0].Value;
							}
							setSubmissionStatus({
								error: response.data.payload === false,
								submitted: response.data.payload !== false,
								message: displayMessage,
							});
						})
						.catch(() => {
							actions.setSubmitting(false);
							setSubmissionStatus({
								error: true,
								submitted: false,
								message: 'Submission was unsuccessful',
							});
						});
					actions.setSubmitting(true);
				}}
			>
				{formik => (
					<Form>
						{sSubmissionStatus?.message && (
							<Alert
								severity={
									sSubmissionStatus?.error
										? 'error'
										: 'success'
								}
								variant='filled'
								onClose={() => {
									setSubmissionStatus(
										prev => (prev.message = '')
									);
								}}
							>
								{sSubmissionStatus?.message}
							</Alert>
						)}
						<Stack
							sx={{ marginTop: '32px' }}
							direction='column'
							spacing={{
								xs: 0.5,
								sm: 0.5,
								md: 1,
							}}
						>
							<FormControlCustomized>
								<HRDepartmentAsyncAutocomplete
									name={JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT}
									key={sDepartmentKey}
									id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT}`}
									label={
										<>
											<RequiredIndicator />
											Department
										</>
									}
									onchange={value => {
										formik.setFieldValue(
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT,
											value
										);
									}}
									onBlur={() =>
										formik.setFieldTouched(
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT,
											true
										)
									}
									value={
										formik.values[
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT
										]
									}
									error={
										!!formik.touched[
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT
										] &&
										!!formik.errors[
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT
										]
									}
									helperText={
										formik.touched[
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT
										] &&
										formik.errors[
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT
										]
									}
									disabled={
										sSubmissionStatus?.submitted ||
										jobCategoryMapping
									}
								/>
							</FormControlCustomized>
							<FormControlCustomized>
								<HRJobTitleAsyncAutocomplete
									key={sJobTitleKey}
									name={JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE}
									id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE}`}
									label={
										<>
											<RequiredIndicator />
											Job Title
										</>
									}
									onchange={value => {
										formik.setFieldValue(
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE,
											value
										);
									}}
									onBlur={() =>
										formik.setFieldTouched(
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE,
											true
										)
									}
									value={
										formik.values[
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE
										]
									}
									error={
										!!formik.touched[
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE
										] &&
										!!formik.errors[
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE
										]
									}
									helperText={
										formik.touched[
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE
										] &&
										formik.errors[
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE
										]
									}
									disabled={
										sSubmissionStatus?.submitted ||
										jobCategoryMapping
									}
								/>
							</FormControlCustomized>
							<FormControlCustomized>
								<Typography variant='h6'>
									<RequiredIndicator />
									Job Categories
								</Typography>
								<FieldArray
									name={
										JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS
									}
								>
									{({ insert, move, remove, push }) => (
										<div>
											{formik.values[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_CATEGORY_IDS
											]?.length > 0 &&
												formik.values[
													JOB_CATEGORY_MAPPING_KEYS
														.JOB_CATEGORY_IDS
												].map((_, index) => (
													<JobCategoryAsyncAutocomplete2
														key={index}
														index={index}
														label={`Search Job Categories`}
														name={`${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS}.${index}`}
														nameBase={
															JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS
														}
														formik={formik}
														onInsert={value => {
															formik.setFieldTouched(
																`${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS}.${index}`,
																true
															);
															insert(value);
														}}
														onBlur={() =>
															formik.setFieldTouched(
																`${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS}.${index}`,
																true
															)
														}
														onRemove={index => {
															formik.setFieldTouched(
																`${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS}.${index}`,
																true
															);
															remove(index);
														}}
														onMove={index => {
															formik.setFieldTouched(
																`${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS}.${index}`,
																true
															);
															move(index);
														}}
														onPush={value => {
															formik.setFieldTouched(
																`${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS}.${index}`,
																true
															);
															push(value);
														}}
														value={
															formik.values[
																JOB_CATEGORY_MAPPING_KEYS
																	.JOB_CATEGORY_IDS
															][index]
														}
														onchange={value => {
															formik.setFieldValue(
																`${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_IDS}.${index}`,
																value
															);
														}}
														disabled={
															sSubmissionStatus?.submitted
														}
														error={
															!!formik.touched[
																JOB_CATEGORY_MAPPING_KEYS
																	.JOB_CATEGORY_IDS
															]?.[index] &&
															!!formik.errors[
																JOB_CATEGORY_MAPPING_KEYS
																	.JOB_CATEGORY_IDS
															]
														}
													/>
												))}
											{formik.values[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_CATEGORY_IDS
											]?.length === 0 && (
												<Button
													variant='contained'
													color='success'
													startIcon={<AddIcon />}
													onClick={() => {
														push('');
													}}
													disabled={
														sSubmissionStatus?.submitted
													}
												>
													Add Additional Job Category
												</Button>
											)}
										</div>
									)}
								</FieldArray>
								<FormHelperText>
									Add up to 4 job categories.
								</FormHelperText>
							</FormControlCustomized>
							<Button
								id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--new`}
								type='submit'
								variant={'contained'}
								disabled={
									(!!formik.errors &&
										Object.keys(formik?.errors).length >
											0) ||
									!formik.dirty ||
									formik.isSubmitting ||
									sSubmissionStatus?.submitted
								}
							>
								{formik.isSubmitting
									? 'Submitting...'
									: sSubmissionStatus?.submitted
									? 'Submitted'
									: 'Create'}
							</Button>
							{sSubmissionStatus?.submitted && (
								<>
									<Button
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--new`}
										variant={'contained'}
										color={'success'}
										onClick={() => {
											formik.resetForm({
												...new JobCategoryMappingNewDTO().extractValues(),
											});
											setDepartmentKey(nanoid());
											setJobTitleKey(nanoid());
											setSubmissionStatus(null);
										}}
									>
										Submit New Job Category Mapping
									</Button>
									<Button
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--home`}
										variant={'contained'}
										color={'primary'}
										onClick={() =>
											history.push('/EpicUtilities/Home')
										}
									>
										Back to Epic Administration Home
									</Button>
								</>
							)}
						</Stack>
					</Form>
				)}
			</Formik>
		</>
	);
};
export default FormView;
